/**
 * AnalysisPage.tsx - Daily model usage and spend analytics
 */

import { useCallback, useEffect, useState } from "react";
import {
  getAdminDailyAnalysis,
  type AdminDailyAnalysis,
} from "../api/adminClient";

const inputClass =
  "px-4 py-2.5 border border-[var(--color-outline-variant)] rounded-lg text-sm bg-[var(--color-surface)] text-[var(--color-on-surface)] focus:outline-none focus:border-[var(--color-accent-blue)] focus:ring-1 focus:ring-[var(--color-accent-blue)] transition-colors transition-shadow duration-150";

function fmtCNY(n: number | undefined | null) {
  return `¥${(n ?? 0).toFixed(2)}`;
}

function fmtTokens(n: number) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function MetricCard({
  label,
  value,
  sub,
  index,
}: {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  index: number;
}) {
  return (
    <div
      className="bg-[var(--color-surface)] rounded-2xl p-6 border border-[var(--color-border-subtle)] card-hover animate-card-enter"
      style={{ "--stagger-index": index } as React.CSSProperties}
    >
      <div className="text-[13px] font-medium text-[var(--color-on-surface-variant)] mb-2">
        {label}
      </div>
      <div className="text-2xl font-semibold text-[var(--color-on-surface)] tabular-nums">
        {value}
      </div>
      {sub && (
        <div className="mt-2 text-xs text-[var(--color-on-surface-variant)]">{sub}</div>
      )}
    </div>
  );
}

function DataTable({
  title,
  empty,
  children,
}: {
  title: string;
  empty: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-[var(--color-surface)] rounded-2xl p-6 border border-[var(--color-border-subtle)] animate-card-enter">
      <h2 className="text-base font-semibold text-[var(--color-on-surface)] mb-4">{title}</h2>
      {empty ? (
        <div className="py-12 text-center text-sm text-[var(--color-on-surface-variant)]">
          当日暂无调用记录
        </div>
      ) : (
        <div className="overflow-x-auto">{children}</div>
      )}
    </div>
  );
}

const thClass =
  "pb-3 pr-4 font-medium text-left text-[var(--color-on-surface-variant)]";
const tdClass = "py-3 pr-4 text-[var(--color-on-surface)]";

interface AnalysisPageProps {
  onAuthError?: () => void;
}

export default function AnalysisPage({ onAuthError }: AnalysisPageProps) {
  const [selectedDate, setSelectedDate] = useState(todayISO);
  const [data, setData] = useState<AdminDailyAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getAdminDailyAnalysis(selectedDate);
      setData(result);
    } catch (e: unknown) {
      setData(null);
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      if (
        msg.includes("401") ||
        msg.includes("403") ||
        msg.includes("管理员")
      ) {
        onAuthError?.();
      }
    } finally {
      setLoading(false);
    }
  }, [selectedDate, onAuthError]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = data?.summary;

  return (
    <div>
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1
            className="text-[28px] font-semibold text-[var(--color-on-surface)] mb-1"
            style={{ letterSpacing: "-0.02em" }}
          >
            用量分析
          </h1>
          <p className="text-[15px] text-[var(--color-on-surface-variant)]">
            查看指定日期的模型调用量与用户消费
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="date"
            value={selectedDate}
            max={todayISO()}
            onChange={(e) => setSelectedDate(e.target.value)}
            className={inputClass}
          />
          <button
            type="button"
            onClick={() => setSelectedDate(todayISO())}
            className="px-4 py-2.5 rounded-lg text-sm font-medium bg-[var(--color-surface-container-low)] text-[var(--color-on-surface)]"
          >
            今天
          </button>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="px-4 py-2.5 rounded-lg text-sm font-medium bg-[var(--color-secondary-container)] text-white disabled:opacity-50"
          >
            {loading ? "加载中..." : "刷新"}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-[var(--color-error-container)] text-[var(--color-on-error-container)] rounded-xl text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <MetricCard
          index={0}
          label="当日消费"
          value={summary ? fmtCNY(summary.total_cost_cny) : "—"}
          sub={
            summary
              ? `成功 ${summary.success_count} 次 · 失败 ${summary.failed_count} 次`
              : undefined
          }
        />
        <MetricCard
          index={1}
          label="API 调用"
          value={summary?.total_requests?.toLocaleString() ?? "—"}
          sub={summary ? `${summary.active_users} 位活跃用户` : undefined}
        />
        <MetricCard
          index={2}
          label="Token 用量"
          value={summary ? fmtTokens(summary.total_tokens) : "—"}
          sub={
            summary
              ? `${summary.total_tokens.toLocaleString()} tokens`
              : undefined
          }
        />
        <MetricCard
          index={3}
          label="当日充值"
          value={summary ? fmtCNY(summary.total_recharge_cny) : "—"}
          sub={
            summary ? `${summary.recharge_count} 笔支付宝充值` : undefined
          }
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
        <DataTable title="按模型" empty={!(data?.by_model?.length)}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border-subtle)]">
                <th className={thClass}>模型</th>
                <th className={thClass}>提供商</th>
                <th className={`${thClass} text-right`}>调用</th>
                <th className={`${thClass} text-right`}>Tokens</th>
                <th className={`${thClass} text-right`}>消费</th>
              </tr>
            </thead>
            <tbody>
              {(data?.by_model ?? []).map((row) => (
                <tr
                  key={`${row.model}-${row.provider}`}
                  className="border-b border-[var(--color-border-subtle)] last:border-0"
                >
                  <td className={`${tdClass} font-medium`}>{row.model}</td>
                  <td className={tdClass}>
                    <span className="px-2 py-0.5 rounded-md text-xs bg-[var(--color-surface-container-low)] text-[var(--color-on-surface-variant)]">
                      {row.provider}
                    </span>
                  </td>
                  <td className={`${tdClass} text-right tabular-nums`}>
                    {row.request_count.toLocaleString()}
                  </td>
                  <td className={`${tdClass} text-right tabular-nums`}>
                    {fmtTokens(row.total_tokens)}
                  </td>
                  <td className={`${tdClass} text-right tabular-nums font-medium`}>
                    {fmtCNY(row.cost_cny)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DataTable>

        <DataTable title="按操作类型" empty={!(data?.by_type?.length)}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border-subtle)]">
                <th className={thClass}>类型</th>
                <th className={`${thClass} text-right`}>调用</th>
                <th className={`${thClass} text-right`}>Tokens</th>
                <th className={`${thClass} text-right`}>消费</th>
              </tr>
            </thead>
            <tbody>
              {(data?.by_type ?? []).map((row) => (
                <tr
                  key={row.request_type}
                  className="border-b border-[var(--color-border-subtle)] last:border-0"
                >
                  <td className={`${tdClass} font-medium font-mono text-xs`}>
                    {row.request_type}
                  </td>
                  <td className={`${tdClass} text-right tabular-nums`}>
                    {row.request_count.toLocaleString()}
                  </td>
                  <td className={`${tdClass} text-right tabular-nums`}>
                    {fmtTokens(row.total_tokens)}
                  </td>
                  <td className={`${tdClass} text-right tabular-nums font-medium`}>
                    {fmtCNY(row.cost_cny)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DataTable>
      </div>

      <DataTable title="按用户消费" empty={!(data?.by_user?.length)}>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--color-border-subtle)]">
              <th className={thClass}>用户</th>
              <th className={thClass}>邮箱</th>
              <th className={`${thClass} text-right`}>调用</th>
              <th className={`${thClass} text-right`}>Tokens</th>
              <th className={`${thClass} text-right`}>消费</th>
            </tr>
          </thead>
          <tbody>
            {(data?.by_user ?? []).map((row) => (
              <tr
                key={row.user_id}
                className="border-b border-[var(--color-border-subtle)] last:border-0"
              >
                <td className={tdClass}>
                  <div className="font-medium">{row.username}</div>
                  <div className="text-xs text-[var(--color-on-surface-variant)]">
                    ID {row.user_id}
                  </div>
                </td>
                <td className={`${tdClass} text-[var(--color-on-surface-variant)]`}>
                  {row.email || "—"}
                </td>
                <td className={`${tdClass} text-right tabular-nums`}>
                  {row.request_count.toLocaleString()}
                </td>
                <td className={`${tdClass} text-right tabular-nums`}>
                  {fmtTokens(row.total_tokens)}
                </td>
                <td className={`${tdClass} text-right tabular-nums font-medium`}>
                  {fmtCNY(row.cost_cny)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </DataTable>
    </div>
  );
}
